import React from 'react';
import { format } from 'date-fns';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UseFormReturn } from 'react-hook-form';
import { PatientFormData } from './patientForm.types';

type BasicFieldKey = 'first_name' | 'last_name' | 'gender' | 'dob';

const DEFAULT_REQUIRED: BasicFieldKey[] = ['first_name', 'last_name', 'gender', 'dob'];

const BasicPatientInfoSection = ({
  form,
  isGuided: _isGuided,
  hideRequiredMarkers = false,
  requiredFields,
}: {
  form: UseFormReturn<PatientFormData>;
  isGuided?: boolean;
  /** Hide every required marker. Ignored when `requiredFields` is provided. */
  hideRequiredMarkers?: boolean;
  /** Per-field required markers. Overrides `hideRequiredMarkers` when set. */
  requiredFields?: BasicFieldKey[];
}) => {
  const requiredSet: Set<BasicFieldKey> = requiredFields
    ? new Set(requiredFields)
    : hideRequiredMarkers
      ? new Set()
      : new Set(DEFAULT_REQUIRED);
  const requiredClass = (field: BasicFieldKey) => (requiredSet.has(field) ? 'required' : undefined);
  return (
  <div className='space-y-4'>
    <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
      Basic Patient Information
    </h3>
    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
      <div className='space-y-1.5'>
        <Label htmlFor='first_name' className={requiredClass('first_name')}>
          First Name
        </Label>
        <Input
          id='first_name'
          name='first_name'
          placeholder='Enter first name'
          {...form.register('first_name')}
          onChange={e => {
            const value = e.target.value || '';
            form.setValue('first_name', value.trimStart(), { shouldValidate: true });
          }}
        />
        {form.formState.errors.first_name && (
          <p className='text-[0.8rem] text-red-500'>{form.formState.errors.first_name.message}</p>
        )}
      </div>
      <div className='space-y-1.5'>
        <Label htmlFor='middle_name'>Middle Name</Label>
        <Input
          id='middle_name'
          name='middle_name'
          placeholder='Enter middle name'
          {...form.register('middle_name')}
          onChange={e => {
            const value = e.target.value || '';
            form.setValue('middle_name', value.trimStart(), { shouldValidate: true });
          }}
        />
        {form.formState.errors.middle_name && (
          <p className='text-[0.8rem] text-red-500'>{form.formState.errors.middle_name.message}</p>
        )}
      </div>
    </div>
    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
      <div className='space-y-1.5'>
        <Label htmlFor='last_name' className={requiredClass('last_name')}>
          Last Name
        </Label>
        <Input
          id='last_name'
          name='last_name'
          placeholder='Enter last name'
          {...form.register('last_name')}
          onChange={e => {
            const value = e.target.value || '';
            form.setValue('last_name', value.trimStart(), { shouldValidate: true });
          }}
        />
        {form.formState.errors.last_name && (
          <p className='text-[0.8rem] text-red-500'>{form.formState.errors.last_name.message}</p>
        )}
      </div>
      <div className='space-y-1.5'>
        <Label htmlFor='gender' className={requiredClass('gender')}>
          Gender
        </Label>
        <Select
          value={form.watch('gender')}
          onValueChange={value => form.setValue('gender', value || '', { shouldValidate: true })}
        >
          <SelectTrigger>
            <SelectValue placeholder='Select gender' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='male'>Male</SelectItem>
            <SelectItem value='female'>Female</SelectItem>
            {/* <SelectItem value='other'>Other</SelectItem> */}
            <SelectItem value='unknown'>Unknown</SelectItem>
          </SelectContent>
        </Select>
        {form.formState.errors.gender && (
          <p className='text-[0.8rem] text-red-500'>{form.formState.errors.gender.message}</p>
        )}
      </div>
    </div>

    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
      <div className='space-y-1.5'>
        <Label htmlFor='dob' className={requiredClass('dob')}>
          Date of Birth
        </Label>
        <DatePicker
          value={
            form.watch('dob')
              ? (() => {
                  const dateStr = form.watch('dob').split('T')[0];
                  const [year, month, day] = dateStr.split('-');
                  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                })()
              : undefined
          }
          onValueChange={date => {
            const dateString = date ? format(new Date(date), 'yyyy-MM-dd') : '';
            form.setValue('dob', dateString + 'T00:00:00.000Z', {
              shouldValidate: true,
            });
          }}
          placeholder='Select date of birth'
          maxDate={new Date()}
          minDate={new Date('1900-01-01')}
        />
        {form.formState.errors.dob && (
          <p className='text-[0.8rem] text-red-500'>{form.formState.errors.dob.message}</p>
        )}
      </div>
      {/* <div className="space-y-1.5">
        <Label htmlFor="species" className="required">Species</Label>
        <Select
          value={form.watch('species')}
          onValueChange={value =>
            form.setValue('species', value || '', { shouldValidate: true })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select species" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="human">Human</SelectItem>
            <SelectItem value="canine">Dog</SelectItem>
            <SelectItem value="feline">Cat</SelectItem>
            <SelectItem value="equine">Horse</SelectItem>
            <SelectItem value="avian">Bird</SelectItem>
            <SelectItem value="mammal - other">Mammal</SelectItem>
            <SelectItem value="reptilian">Reptile</SelectItem>
            <SelectItem value="swine">Pig</SelectItem>
          </SelectContent>
        </Select>
        {form.formState.errors.species && (
          <p className="text-[0.8rem] text-red-500">
            {form.formState.errors.species.message}
          </p>
        )}
      </div>
      {form.watch('species') !== 'human' &&
        form.watch('species') !== '' &&
        form.watch('species') && (
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="last_name">Animal Owner Name</Label>
              <Input
                id="animal_owner_name"
                name="animal_owner_name"
                placeholder='Enter animal owner name'
                {...form.register('animal_owner_name')}
              />
              {form.formState.errors.animal_owner_name && (
                <p className="text-[0.8rem] text-red-500">
                  {form.formState.errors.animal_owner_name.message}
                </p>
              )}
            </div>
          </div>
        )} */}
    </div>
  </div>
  );
};

export default BasicPatientInfoSection;
